import { z } from "zod";
import { Id, Timestamp } from "./common.js";

export const MAX_NOTE_LENGTH = 500_000;
/** spec09: a canvas (JSON) may be up to 5 MB, with stroke and point limits (FR12). */
export const MAX_CANVAS_LENGTH = 5 * 1024 * 1024;
export const MAX_CANVAS_STROKES = 20_000;
export const MAX_STROKE_POINTS = 5_000;
/** Canvas coordinates are relative to a page 1000 units wide (spec09 FR8). */
export const CANVAS_PAGE_WIDTH = 1000;

const NotebookName = z.string().trim().min(1).max(100);

export const Notebook = z.object({
  id: Id,
  parentId: Id.nullable(),
  name: z.string(),
  /** Notes directly in this notebook (not in sub-notebooks). */
  noteCount: z.number().int(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Notebook = z.infer<typeof Notebook>;

export const NotebookList = z.object({ notebooks: z.array(Notebook) });

export const CreateNotebook = z.object({
  /** spec11: the app picks the id offline; creating the same id again is a no-op. */
  id: Id.optional(),
  name: NotebookName,
  parentId: Id.nullish(),
});
export type CreateNotebook = z.infer<typeof CreateNotebook>;

/** Rename and/or move (parentId null = top level). */
export const UpdateNotebook = z
  .object({ name: NotebookName, parentId: Id.nullable() })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to update" });
export type UpdateNotebook = z.infer<typeof UpdateNotebook>;

export const NoteKind = z.enum(["markdown", "canvas"]);
export type NoteKind = z.infer<typeof NoteKind>;

/** A note in lists: no content, just what the row shows. */
export const NoteSummary = z.object({
  id: Id,
  kind: NoteKind,
  title: z.string(),
  preview: z.string(),
  notebookId: Id.nullable(),
  pinned: z.boolean(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type NoteSummary = z.infer<typeof NoteSummary>;

export const NoteList = z.object({ notes: z.array(NoteSummary) });

export const Backlink = z.object({ noteId: Id, title: z.string(), line: z.string() });
export type Backlink = z.infer<typeof Backlink>;

/** A `[[link]]` in the note and the note it resolves to (null = missing). */
export const ResolvedLink = z.object({ target: z.string(), noteId: Id.nullable() });
export type ResolvedLink = z.infer<typeof ResolvedLink>;

export const Note = NoteSummary.extend({
  content: z.string(),
  /** Incremented on every save; a save must name the version it is based on. */
  version: z.number().int(),
  links: z.array(ResolvedLink),
  backlinks: z.array(Backlink),
});
export type Note = z.infer<typeof Note>;

/** The kind-specific limit (MAX_NOTE_LENGTH for Markdown) is checked by the server. */
const Content = z.string().max(MAX_CANVAS_LENGTH);
/** The title field (spec04 FR9); empty shows as "Untitled". */
const Title = z.string().trim().max(200);

export const CreateNote = z.object({
  /** spec11: the app picks the id offline (UUIDv7); creating the same id again is a no-op. */
  id: Id.optional(),
  kind: NoteKind.optional(),
  notebookId: Id.nullish(),
  title: Title.optional(),
  content: Content.optional(),
});
export type CreateNote = z.infer<typeof CreateNote>;

/** Saving content needs `baseVersion` (409 when the note changed since); the title does not. */
export const UpdateNote = z
  .object({
    title: Title,
    content: Content,
    baseVersion: z.number().int().min(1),
    pinned: z.boolean(),
    notebookId: Id.nullable(),
  })
  .partial()
  .refine((p) => p.content === undefined || p.baseVersion !== undefined, {
    message: "Saving content needs baseVersion",
    path: ["baseVersion"],
  })
  .refine((p) => Object.keys(p).some((k) => k !== "baseVersion"), { message: "Nothing to update" });
export type UpdateNote = z.infer<typeof UpdateNote>;

export const NoteParams = z.object({ id: Id });

/** spec11: one page of changes for the app's offline copy. */
export const NotesSyncQuery = z.object({
  /** `serverTime` of the last complete sync; left out = everything. */
  since: Timestamp.optional(),
  /** `nextCursor` of the previous page. */
  cursor: Id.optional(),
});
export type NotesSyncQuery = z.infer<typeof NotesSyncQuery>;

/** Every note (canvases and the trash too) as the list shows it, with version and trash state. */
export const NoteIndexEntry = NoteSummary.extend({
  version: z.number().int(),
  deletedAt: Timestamp.nullable(),
});
export type NoteIndexEntry = z.infer<typeof NoteIndexEntry>;

/** A notebook in the app's offline copy, in the trash or not (counts are computed there). */
export const SyncNotebook = Notebook.omit({ noteCount: true }).extend({
  deletedAt: Timestamp.nullable(),
});
export type SyncNotebook = z.infer<typeof SyncNotebook>;

export const NotesSync = z.object({
  /** All notebooks of the user; ids missing here were deleted for good. */
  notebooks: z.array(SyncNotebook),
  /** All notes; ids missing here were purged or deleted for good on the server. */
  index: z.array(NoteIndexEntry),
  /** Content of the notes (Markdown and canvases) changed since `since` (one page). */
  notes: z.array(z.object({ id: Id, content: z.string(), version: z.number().int() })),
  /** More changed notes follow: ask again with this cursor. */
  nextCursor: Id.nullable(),
  /** Pass as `since` next time. */
  serverTime: Timestamp,
});
export type NotesSync = z.infer<typeof NotesSync>;
export const NotebookParams = z.object({ id: Id });

/** Note list filter: one notebook (direct notes only), pinned notes, or all. */
export const NotesQuery = z.object({
  notebook: Id.optional(),
  pinned: z.enum(["true"]).optional(),
});
export type NotesQuery = z.infer<typeof NotesQuery>;

export const SearchQuery = z.object({
  q: z.string().trim().min(2).max(200),
  notebook: Id.optional(),
});
export type SearchQuery = z.infer<typeof SearchQuery>;

/** The matching line split into plain and matched parts (no HTML). */
export const SearchResult = z.object({
  id: Id,
  title: z.string(),
  notebookId: Id.nullable(),
  snippet: z.array(z.object({ text: z.string(), match: z.boolean() })),
});
export type SearchResult = z.infer<typeof SearchResult>;

export const SearchResults = z.object({ results: z.array(SearchResult) });

export const TitlesQuery = z.object({ prefix: z.string().max(200) });
export const NoteTitles = z.object({
  titles: z.array(z.object({ id: Id, title: z.string() })),
});
export type NoteTitles = z.infer<typeof NoteTitles>;

export const TrashItem = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("note"),
    id: Id,
    title: z.string(),
    deletedAt: Timestamp,
  }),
  z.object({
    kind: z.literal("notebook"),
    id: Id,
    title: z.string(),
    /** Notes that went to the trash with it (including sub-notebooks). */
    noteCount: z.number().int(),
    deletedAt: Timestamp,
  }),
]);
export type TrashItem = z.infer<typeof TrashItem>;

export const TrashList = z.object({ items: z.array(TrashItem) });

export const TrashParams = z.object({ kind: z.enum(["note", "notebook"]), id: Id });

// --- Canvas (spec09) ---

/** The pen colours, stored as keys (tokens --color-ink-*), never as hex (FR4). */
export const InkColor = z.enum(["white", "blue", "red", "green", "yellow"]);
export type InkColor = z.infer<typeof InkColor>;

/** Thin, medium, thick. */
export const InkWidth = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type InkWidth = z.infer<typeof InkWidth>;

/** [x, y, pressure]: x across the page (0–1000), y down from the top, pressure 0–1. */
const InkPoint = z.tuple([
  z.number().min(0).max(CANVAS_PAGE_WIDTH),
  z.number().min(0).max(10_000_000),
  z.number().min(0).max(1),
]);

// Strict: the server stores the JSON as sent, so unknown keys are refused, not dropped.
export const InkStroke = z.strictObject({
  id: z.string().min(1).max(40),
  color: InkColor,
  width: InkWidth,
  points: z.array(InkPoint).min(1).max(MAX_STROKE_POINTS),
});
export type InkStroke = z.infer<typeof InkStroke>;

/** A canvas note's content (JSON): strokes only (spec09). */
export const CanvasDocument = z.strictObject({
  type: z.literal("ronin-ink"),
  version: z.literal(1),
  strokes: z.array(InkStroke).max(MAX_CANVAS_STROKES),
});
export type CanvasDocument = z.infer<typeof CanvasDocument>;

export const EMPTY_CANVAS = JSON.stringify({
  type: "ronin-ink",
  version: 1,
  strokes: [],
} satisfies CanvasDocument);

/** A canvas note's content parsed and checked; null when it is not a valid canvas. */
export function parseCanvas(content: string): CanvasDocument | null {
  if (content.length > MAX_CANVAS_LENGTH) return null;
  try {
    const result = CanvasDocument.safeParse(JSON.parse(content));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
